import React, { useEffect, useState } from "react";
import { Edit3, Loader2, Plus, RefreshCw, Trash2, X } from "lucide-react";
import useBulkPackages from "../../services/Order/useBulkPackages";
import useBulkCatalogue from "../../services/Order/useBulkCatalogue";

const inputClass =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400";

const EMPTY_PACKAGE = {
  package_id: "",
  catalogue_id: "",
  fk_menuid: "",
  index_sequence: "",
  page_from: "",
  page_to: "",
  is_index_item: "N",
  parent_package_id: "",
  package_name: "",
  package_type: "",
  description: "",
  pricing_method: "",
  minimum_persons: "",
  maximum_persons: "",
  person_increment: "",
  base_price: "",
  display_order: "",
  img_url: "",
  status: "ACTIVE",
  serving_type: "",
};

const EMPTY_VARIANT = {
  package_variant_id: "",
  package_id: "",
  variant_name: "",
  minimum_persons: "",
  maximum_persons: "",
  exact_persons: "",
  price: "",
  status: "ACTIVE",
};

const EMPTY_MAPPING = {
  package_item_mapping_id: "",
  package_id: "",
  item_id: "",
  display_order: "",
  status: "ACTIVE",
};

export function BulkPackageList() {
  const {
    getPackages,
    insertPackage,
    updatePackage,
    deletePackage,
    packagesLoading,
    packageSaving,
    packageDeletingId,
  } = useBulkPackages();
  const { getCatalogues, getBulkMenus } = useBulkCatalogue();

  const [rows, setRows] = useState([]);
  const [catalogues, setCatalogues] = useState([]);
  const [menus, setMenus] = useState([]);
  const [form, setForm] = useState(EMPTY_PACKAGE);
  const [modal, setModal] = useState(false);

  const load = async () => {
    const packageRows = await getPackages("");
    setRows(Array.isArray(packageRows) ? packageRows : []);
  };
  const ensurePackageOptions = async () => {
    const [c, m] = await Promise.all([
      catalogues.length ? Promise.resolve(catalogues) : getCatalogues(""),
      menus.length ? Promise.resolve(menus) : getBulkMenus(""),
    ]);
    setCatalogues(Array.isArray(c) ? c : []);
    setMenus(Array.isArray(m) ? m : []);
  };

  useEffect(() => {
    load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const edit = async (r) => {
    await ensurePackageOptions();
    setForm(
      Object.keys(EMPTY_PACKAGE).reduce(
        (acc, key) => ({
          ...acc,
          [key]: String(r?.[key] ?? EMPTY_PACKAGE[key]),
        }),
        {},
      ),
    );
    setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const saved = form.package_id
      ? await updatePackage(form)
      : await insertPackage(form);
    if (saved) {
      setModal(false);
      setForm(EMPTY_PACKAGE);
      load();
    }
  };

  return (
    <section className="space-y-3">
      <Header
        title="Packages"
        subtitle="Manage bulk catalogue packages"
        loading={packagesLoading}
        onRefresh={load}
        onAdd={async () => {
          await ensurePackageOptions();
          setForm(EMPTY_PACKAGE);
          setModal(true);
        }}
        add="Add Package"
      />
      <Rows
        loading={packagesLoading}
        empty="No packages found."
        rows={rows}
        render={(r, i) => (
          <div
            key={r?.package_id ?? i}
            className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
          >
            {r?.img_url ? (
              <img
                src={r.img_url}
                alt=""
                className="h-12 w-12 rounded-lg object-cover"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <b className="text-sm text-slate-900">
                {r?.package_name || "Unnamed Package"}
              </b>
              <p className="text-[11px] text-slate-500">
                ID #{r?.package_id || "—"} · Type {r?.package_type || "—"} ·
                Base ₹{r?.base_price || "—"} · {r?.status || "—"}
              </p>
            </div>
            <Icon onClick={() => edit(r)}>
              <Edit3 size={13} />
            </Icon>
            <Icon
              danger
              disabled={packageDeletingId === String(r?.package_id)}
              onClick={async () => {
                if (await deletePackage(r?.package_id)) load();
              }}
            >
              {packageDeletingId === String(r?.package_id) ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Trash2 size={13} />
              )}
            </Icon>
          </div>
        )}
      />

      {modal && (
        <Modal
          title={form.package_id ? "Edit Package" : "Add Package"}
          close={() => setModal(false)}
          wide
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
              <Select
                label="Catalogue"
                name="catalogue_id"
                value={form.catalogue_id}
                set={setForm}
              >
                <option value="">Select Catalogue</option>
                {catalogues.map((c) => (
                  <option
                    key={c?.catalogue_id ?? c?.id}
                    value={c?.catalogue_id ?? c?.id}
                  >
                    {c?.catalogue_name ||
                      `Catalogue #${c?.catalogue_id ?? c?.id}`}
                  </option>
                ))}
              </Select>
              <Select
                label="Menu"
                name="fk_menuid"
                value={form.fk_menuid}
                set={setForm}
              >
                <option value="">Select Menu</option>
                {menus.map((m) => (
                  <option key={m?.id} value={m?.id}>
                    {m?.menu_name || `Menu #${m?.id}`}
                  </option>
                ))}
              </Select>
              <Input
                label="Package Name"
                name="package_name"
                value={form.package_name}
                set={setForm}
                required
              />
              <Input
                label="Package Type"
                name="package_type"
                value={form.package_type}
                set={setForm}
              />
              <Input
                label="Pricing Method"
                name="pricing_method"
                value={form.pricing_method}
                set={setForm}
              />
              <Input
                label="Serving Type"
                name="serving_type"
                value={form.serving_type}
                set={setForm}
              />
              <Input
                label="Index Sequence"
                name="index_sequence"
                value={form.index_sequence}
                set={setForm}
              />
              <Input
                label="Page From"
                name="page_from"
                value={form.page_from}
                set={setForm}
              />
              <Input
                label="Page To"
                name="page_to"
                value={form.page_to}
                set={setForm}
              />
              <Select
                label="Is Index Item"
                name="is_index_item"
                value={form.is_index_item}
                set={setForm}
              >
                <option value="Y">Y</option>
                <option value="N">N</option>
              </Select>
              <Input
                label="Parent Package ID"
                name="parent_package_id"
                value={form.parent_package_id}
                set={setForm}
              />
              <Input
                label="Minimum Persons"
                name="minimum_persons"
                value={form.minimum_persons}
                set={setForm}
                type="number"
              />
              <Input
                label="Maximum Persons"
                name="maximum_persons"
                value={form.maximum_persons}
                set={setForm}
                type="number"
              />
              <Input
                label="Person Increment"
                name="person_increment"
                value={form.person_increment}
                set={setForm}
                type="number"
              />
              <Input
                label="Base Price"
                name="base_price"
                value={form.base_price}
                set={setForm}
                type="number"
              />
              <Input
                label="Display Order"
                name="display_order"
                value={form.display_order}
                set={setForm}
                type="number"
              />
              <Input
                label="Image URL"
                name="img_url"
                value={form.img_url}
                set={setForm}
              />
              <Select
                label="Status"
                name="status"
                value={form.status}
                set={setForm}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </Select>
              <label className="sm:col-span-2 lg:col-span-3">
                <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                  Description
                </span>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                  className="min-h-20 w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:border-emerald-400"
                />
              </label>
            </div>
            <Footer
              saving={packageSaving}
              close={() => setModal(false)}
              edit={!!form.package_id}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

export function BulkPackageVariants() {
  const {
    getPackages,
    getPackageVariants,
    insertPackageVariant,
    updatePackageVariant,
    deletePackageVariant,
    packageVariantsLoading,
    packageVariantSaving,
    packageVariantDeletingId,
  } = useBulkPackages();
  const [packages, setPackages] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_VARIANT);
  const [modal, setModal] = useState(false);

  const load = async () => {
    const r = await getPackageVariants("");
    setRows(Array.isArray(r) ? r : []);
  };
  const ensurePackages = async () => {
    if (packages.length) return packages;
    const r = await getPackages("");
    const next = Array.isArray(r) ? r : [];
    setPackages(next);
    return next;
  };
  useEffect(() => {
    load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const edit = async (r) => {
    await ensurePackages();
    setForm(
      Object.keys(EMPTY_VARIANT).reduce(
        (a, k) => ({ ...a, [k]: String(r?.[k] ?? EMPTY_VARIANT[k]) }),
        {},
      ),
    );
    setModal(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const saved = form.package_variant_id
      ? await updatePackageVariant(form)
      : await insertPackageVariant(form);
    if (saved) {
      setModal(false);
      setForm(EMPTY_VARIANT);
      load();
    }
  };
  const packageName = (id) =>
    packages.find((p) => String(p?.package_id) === String(id))?.package_name ||
    `Package #${id || "—"}`;

  return (
    <section className="space-y-3">
      <Header
        title="Package Variants"
        subtitle="Manage package person/price variants"
        loading={packageVariantsLoading}
        onRefresh={load}
        onAdd={async () => {
          const p = await ensurePackages();
          setForm({
            ...EMPTY_VARIANT,
            package_id: String(p?.[0]?.package_id ?? ""),
          });
          setModal(true);
        }}
        add="Add Variant"
      />
      <Rows
        loading={packageVariantsLoading}
        empty="No package variants found."
        rows={rows}
        render={(r, i) => (
          <div
            key={r?.package_variant_id ?? i}
            className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
          >
            <div className="min-w-0 flex-1">
              <b className="text-sm">{r?.variant_name || "Unnamed Variant"}</b>
              <p className="text-[11px] text-slate-500">
                ID #{r?.package_variant_id || "—"} ·{" "}
                {packageName(r?.package_id)} · Price ₹{r?.price || "—"} ·{" "}
                {r?.status || "—"}
              </p>
            </div>
            <Icon onClick={() => edit(r)}>
              <Edit3 size={13} />
            </Icon>
            <Icon
              danger
              disabled={
                packageVariantDeletingId === String(r?.package_variant_id)
              }
              onClick={async () => {
                if (await deletePackageVariant(r?.package_variant_id)) load();
              }}
            >
              {packageVariantDeletingId === String(r?.package_variant_id) ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Trash2 size={13} />
              )}
            </Icon>
          </div>
        )}
      />
      {modal && (
        <Modal
          title={
            form.package_variant_id
              ? "Edit Package Variant"
              : "Add Package Variant"
          }
          close={() => setModal(false)}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <Select
                label="Package"
                name="package_id"
                value={form.package_id}
                set={setForm}
              >
                <option value="">Select Package</option>
                {packages.map((p) => (
                  <option key={p?.package_id} value={p?.package_id}>
                    {p?.package_name || `Package #${p?.package_id}`}
                  </option>
                ))}
              </Select>
              <Input
                label="Variant Name"
                name="variant_name"
                value={form.variant_name}
                set={setForm}
                required
              />
              <Input
                label="Minimum Persons"
                name="minimum_persons"
                value={form.minimum_persons}
                set={setForm}
                type="number"
              />
              <Input
                label="Maximum Persons"
                name="maximum_persons"
                value={form.maximum_persons}
                set={setForm}
                type="number"
              />
              <Input
                label="Exact Persons"
                name="exact_persons"
                value={form.exact_persons}
                set={setForm}
                type="number"
              />
              <Input
                label="Price"
                name="price"
                value={form.price}
                set={setForm}
                type="number"
              />
              <Select
                label="Status"
                name="status"
                value={form.status}
                set={setForm}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </Select>
            </div>
            <Footer
              saving={packageVariantSaving}
              close={() => setModal(false)}
              edit={!!form.package_variant_id}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

export function BulkPackageItemMappings() {
  const {
    getPackages,
    getPackageItemMappings,
    insertPackageItemMapping,
    updatePackageItemMapping,
    deletePackageItemMapping,
    packageItemMappingsLoading,
    packageItemMappingSaving,
    packageItemMappingDeletingId,
  } = useBulkPackages();
  const { getItems } = useBulkCatalogue();
  const [packages, setPackages] = useState([]);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_MAPPING);
  const [modal, setModal] = useState(false);

  const load = async () => {
    const r = await getPackageItemMappings("");
    setRows(Array.isArray(r) ? r : []);
  };
  const ensureMappingOptions = async () => {
    const [pRaw, iRaw] = await Promise.all([
      packages.length ? Promise.resolve(packages) : getPackages(""),
      items.length ? Promise.resolve(items) : getItems("", ""),
    ]);
    const p = Array.isArray(pRaw) ? pRaw : [],
      i = Array.isArray(iRaw) ? iRaw : [];
    setPackages(p);
    setItems(i);
    return { p, i };
  };
  useEffect(() => {
    load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const edit = async (r) => {
    await ensureMappingOptions();
    setForm(
      Object.keys(EMPTY_MAPPING).reduce(
        (a, k) => ({ ...a, [k]: String(r?.[k] ?? EMPTY_MAPPING[k]) }),
        {},
      ),
    );
    setModal(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const saved = form.package_item_mapping_id
      ? await updatePackageItemMapping(form)
      : await insertPackageItemMapping(form);
    if (saved) {
      setModal(false);
      setForm(EMPTY_MAPPING);
      load();
    }
  };
  const packageName = (id) =>
    packages.find((p) => String(p?.package_id) === String(id))?.package_name ||
    `Package #${id || "—"}`;
  const itemName = (id) =>
    items.find((x) => String(x?.item_id) === String(id))?.item_name ||
    `Item #${id || "—"}`;

  return (
    <section className="space-y-3">
      <Header
        title="Package Item Mapping"
        subtitle="Map catalogue items to packages"
        loading={packageItemMappingsLoading}
        onRefresh={load}
        onAdd={async () => {
          const { p, i } = await ensureMappingOptions();
          setForm({
            ...EMPTY_MAPPING,
            package_id: String(p?.[0]?.package_id ?? ""),
            item_id: String(i?.[0]?.item_id ?? ""),
          });
          setModal(true);
        }}
        add="Add Mapping"
      />
      <Rows
        loading={packageItemMappingsLoading}
        empty="No package item mappings found."
        rows={rows}
        render={(r, i) => (
          <div
            key={r?.package_item_mapping_id ?? i}
            className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
          >
            <div className="min-w-0 flex-1">
              <b className="text-sm">{packageName(r?.package_id)}</b>
              <p className="text-[11px] text-slate-500">
                Mapping #{r?.package_item_mapping_id || "—"} ·{" "}
                {itemName(r?.item_id)} · Order {r?.display_order || "—"} ·{" "}
                {r?.status || "—"}
              </p>
            </div>
            <Icon onClick={() => edit(r)}>
              <Edit3 size={13} />
            </Icon>
            <Icon
              danger
              disabled={
                packageItemMappingDeletingId ===
                String(r?.package_item_mapping_id)
              }
              onClick={async () => {
                if (await deletePackageItemMapping(r?.package_item_mapping_id))
                  load();
              }}
            >
              {packageItemMappingDeletingId ===
              String(r?.package_item_mapping_id) ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Trash2 size={13} />
              )}
            </Icon>
          </div>
        )}
      />
      {modal && (
        <Modal
          title={
            form.package_item_mapping_id
              ? "Edit Package Item Mapping"
              : "Add Package Item Mapping"
          }
          close={() => setModal(false)}
        >
          <form onSubmit={submit}>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
              <Select
                label="Package"
                name="package_id"
                value={form.package_id}
                set={setForm}
              >
                <option value="">Select Package</option>
                {packages.map((p) => (
                  <option key={p?.package_id} value={p?.package_id}>
                    {p?.package_name || `Package #${p?.package_id}`}
                  </option>
                ))}
              </Select>
              <Select
                label="Item"
                name="item_id"
                value={form.item_id}
                set={setForm}
              >
                <option value="">Select Item</option>
                {items.map((i) => (
                  <option key={i?.item_id} value={i?.item_id}>
                    {i?.item_name || `Item #${i?.item_id}`}
                  </option>
                ))}
              </Select>
              <Input
                label="Display Order"
                name="display_order"
                value={form.display_order}
                set={setForm}
                type="number"
              />
              <Select
                label="Status"
                name="status"
                value={form.status}
                set={setForm}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </Select>
            </div>
            <Footer
              saving={packageItemMappingSaving}
              close={() => setModal(false)}
              edit={!!form.package_item_mapping_id}
            />
          </form>
        </Modal>
      )}
    </section>
  );
}

function Header({ title, subtitle, loading, onRefresh, onAdd, add }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="text-[11px] text-slate-500">{subtitle}</p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 px-3 text-xs"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
        >
          <Plus size={13} />
          {add}
        </button>
      </div>
    </div>
  );
}
function Rows({ loading, empty, rows, render }) {
  if (loading)
    return <div className="h-24 animate-pulse rounded-xl border bg-white" />;
  if (!rows?.length)
    return (
      <div className="rounded-xl border border-dashed bg-white p-12 text-center text-sm text-slate-500">
        {empty}
      </div>
    );
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {rows.map(render)}
    </div>
  );
}
function Modal({ title, close, children, wide }) {
  return (
    <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3">
      <div
        className={`max-h-[94vh] w-full overflow-y-auto rounded-2xl bg-white shadow-2xl ${wide ? "max-w-5xl" : "max-w-2xl"}`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-4 py-3">
          <h3 className="font-semibold">{title}</h3>
          <button type="button" onClick={close}>
            <X size={17} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Input({ label, name, value, set, type = "text", required = false }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <input
        type={type}
        required={required}
        name={name}
        value={value}
        onChange={(e) => set((p) => ({ ...p, [name]: e.target.value }))}
        className={inputClass}
      />
    </label>
  );
}
function Select({ label, name, value, set, children }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <select
        name={name}
        value={value}
        onChange={(e) => set((p) => ({ ...p, [name]: e.target.value }))}
        className={inputClass}
      >
        {children}
      </select>
    </label>
  );
}
function Icon({ children, danger, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`flex h-8 w-8 items-center justify-center rounded-lg border ${danger ? "border-rose-100 bg-rose-50 text-rose-600" : "border-slate-200 text-slate-500"}`}
    >
      {children}
    </button>
  );
}
function Footer({ saving, close, edit }) {
  return (
    <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-slate-50 px-4 py-3">
      <button
        type="button"
        onClick={close}
        disabled={saving}
        className="h-9 rounded-lg border bg-white px-4 text-xs font-semibold"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={saving}
        className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
      >
        {saving ? (
          <Loader2 size={13} className="animate-spin" />
        ) : edit ? (
          <Edit3 size={13} />
        ) : (
          <Plus size={13} />
        )}{" "}
        {saving ? "Saving..." : edit ? "Update" : "Add"}
      </button>
    </div>
  );
}
